/**
 * Regras de visibilidade das opções do menu, do lado do painel.
 *
 * Espelha `REGRAS_DE_VISIBILIDADE` do servidor (`src/core/menu/persistencia.ts`).
 * São dois pacotes que não se importam, então a tabela existe nos dois lados —
 * e os dois têm teste. O que não pode é a tela dizer uma coisa e o bot fazer
 * outra.
 */

/** Quem está olhando, na prévia. */
export type PerfilDaPrevia = 'aluno' | 'instrutor' | 'interessado';

/** As regras que só valem com `MENU_POR_TIPO_ATIVO` ligado. */
export const REGRAS_DE_TIPO = ['aluno', 'instrutor', 'aluno_ou_instrutor', 'interessado'];

/**
 * Rótulo curto, para o selo da opção.
 *
 * Curto de propósito: o selo divide a linha com o destino e a contagem de
 * caracteres. "só aparece: aluno identificado" empurrava os outros para fora.
 */
export const ROTULO_REGRA: Record<string, string> = {
  em_horario: 'só em horário',
  fora_do_horario: 'só fora do horário',
  aluno: 'só aluno',
  instrutor: 'só instrutor',
  aluno_ou_instrutor: 'aluno e instrutor',
  interessado: 'só interessado',
};

/**
 * Marca compacta, para a coluna da árvore.
 *
 * Uma ou duas letras ao lado do número da opção — é ali que se vê o CONJUNTO,
 * e o conjunto é o que importa na hora de decidir quem vê o quê. O nome
 * inteiro fica no tooltip.
 *
 * Letra, e não cor: a cor é reforço. Quem não distingue cores, ou está num
 * tema escuro, lê a letra do mesmo jeito.
 */
export const MARCA_REGRA: Record<string, string> = {
  em_horario: '☀',
  fora_do_horario: '☾',
  aluno: 'A',
  instrutor: 'I',
  aluno_ou_instrutor: 'AI',
  interessado: '?',
};

/** Nome por extenso, para o tooltip. */
export const DESCRICAO_REGRA: Record<string, string> = {
  em_horario: 'só aparece dentro do horário de atendimento',
  fora_do_horario: 'só aparece fora do horário de atendimento',
  aluno: 'só aparece para aluno identificado',
  instrutor: 'só aparece para instrutor identificado',
  aluno_ou_instrutor: 'só aparece para aluno ou instrutor identificado',
  interessado: 'só aparece para quem não está no SAGA',
};

/**
 * Regra que NÃO esconde nada hoje.
 *
 * Com `MENU_POR_TIPO_ATIVO` desligado, as regras de tipo ficam inertes. O selo
 * precisa dizer isso — senão quem edita marca "só aluno", publica, e acha que
 * escondeu algo que continua à vista de todos.
 */
export function regraInerte(regra: string, menuPorTipoAtivo: boolean): boolean {
  return !menuPorTipoAtivo && REGRAS_DE_TIPO.includes(regra);
}

/**
 * A opção aparece, para este horário e este perfil?
 *
 * ERA UM BUG: o filtro antigo comparava `(horario === 'fora') === (regra ===
 * 'fora_do_horario')`, o que fazia uma opção marcada como "só aluno"
 * DESAPARECER da prévia ao ver "fora do horário" — as duas famílias de regra
 * não têm nada a ver uma com a outra. O filtro nasceu quando só havia regra de
 * horário e não acompanhou as de tipo.
 *
 * Regra desconhecida NÃO esconde, igual ao servidor: perder um item do menu em
 * silêncio é pior que mostrar um que deveria estar escondido.
 */
export function opcaoVisivel(
  regra: string | null,
  contexto: {
    horario: 'em' | 'fora';
    perfil: PerfilDaPrevia;
    menuPorTipoAtivo: boolean;
  },
): boolean {
  if (!regra) return true;

  if (regra === 'em_horario') return contexto.horario === 'em';
  if (regra === 'fora_do_horario') return contexto.horario === 'fora';

  // Desligado, regra de tipo não esconde ninguém.
  if (!contexto.menuPorTipoAtivo) return true;

  if (regra === 'aluno') return contexto.perfil === 'aluno';
  if (regra === 'instrutor') return contexto.perfil === 'instrutor';
  if (regra === 'aluno_ou_instrutor') {
    return contexto.perfil === 'aluno' || contexto.perfil === 'instrutor';
  }
  if (regra === 'interessado') return contexto.perfil === 'interessado';

  return true;
}

/**
 * Marca e rótulo da AUSÊNCIA de regra.
 *
 * Só aparecem quando há contraste a mostrar — ver `nóDiferencia`.
 */
export const MARCA_TODOS = 'T';
export const ROTULO_TODOS = 'todos';

/**
 * Este nó diferencia quem vê o quê?
 *
 * É a condição para o selo "todos" aparecer nas opções SEM regra. Sem ela,
 * "todos" em sete de oito linhas de todo nó seria ruído — e nó em que ninguém
 * marcou nada não tem decisão a tomar.
 *
 * Repara que a pergunta é por regra VALENDO, não por regra gravada. Com
 * `MENU_POR_TIPO_ATIVO` desligado, uma opção marcada "só aluno" aparece para
 * todo mundo: chamar as vizinhas de "todos" anunciaria um contraste que não
 * existe. Já as regras de HORÁRIO nunca são inertes, então um nó com só elas
 * diferencia de verdade, com o interruptor ligado ou não.
 */
export function noDiferencia(
  opcoes: readonly { visivelRegra: string | null }[],
  menuPorTipoAtivo: boolean,
): boolean {
  return opcoes.some((o) => o.visivelRegra && !regraInerte(o.visivelRegra, menuPorTipoAtivo));
}
